import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import SafeIcon from '../../common/SafeIcon';
import { avatarPublicUrl } from '../../lib/memberHelpers';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const REACTIONS = [
  { type: 'like', emoji: '👍' },
  { type: 'love', emoji: '❤️' },
  { type: 'haha', emoji: '😄' },
  { type: 'mate', emoji: '🧉' },
  { type: 'mano', emoji: '🤌' },
];

export default function Comments({ itemId, itemType }) {
  const { comments, members, currentUser, addComment, commentReactions, toggleCommentReaction } = useApp();
  const [text, setText] = useState('');
  const [activePicker, setActivePicker] = useState(null); // Stores the ID of the comment whose picker is open

  const itemComments = comments
    .filter(c => c.item_id === itemId)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await addComment(itemId, itemType, text);
      setText('');
    } catch (err) {
      console.error('addComment failed:', err);
    }
  };

  return (
    <div className="mt-12 bg-white rounded-[32px] p-6 md:p-10 border border-stone-100 shadow-sm">
      <h3 className="text-2xl font-editorial font-bold text-[#1B2A4E] mb-6 flex items-center gap-3">
        <SafeIcon name="MessageSquare" className="w-6 h-6 text-[#74ACDF]" />
        Comentarios ({itemComments.length})
      </h3>

      <div className="space-y-6 mb-8 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
        {itemComments.length > 0 ? itemComments.map(comment => {
          const author = members.find(m => m.id === comment.member_id);
          
          // Reactions logic for this comment
          const cReactions = commentReactions.filter(cr => cr.comment_id === comment.id);
          const userReaction = cReactions.find(cr => cr.member_id === currentUser.id);
          
          const groupedReactions = cReactions.reduce((acc, cr) => {
            acc[cr.type] = (acc[cr.type] || 0) + 1;
            return acc;
          }, {});

          return (
            <div key={comment.id} className="flex gap-4 group">
              <img 
                src={avatarPublicUrl(author?.foto_url) || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(author?.nombre || '?')}`} 
                alt={author?.nombre} 
                className="w-10 h-10 rounded-full object-cover shrink-0 ring-2 ring-stone-100" 
              />
              <div className="flex-1">
                <div className="bg-stone-50 rounded-2xl rounded-tl-none p-4 border border-stone-100 relative">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-sm text-[#1B2A4E]">{author?.nombre}</span>
                    <span className="text-xs text-stone-400 label-technical">
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: es })}
                    </span>
                  </div>
                  <p className="text-stone-600 text-sm leading-relaxed">{comment.text}</p>
                </div>

                {/* Reaction Summary & Picker Toggle */}
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5 ml-1 mb-4 relative z-10">
                  {Object.entries(groupedReactions).map(([type, count]) => {
                    const hasReacted = userReaction?.type === type;
                    return (
                      <button
                        key={type}
                        onClick={async () => {
                          try {
                            await toggleCommentReaction(comment.id, type);
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border transition-colors ${hasReacted ? 'bg-[#74ACDF]/10 border-[#74ACDF]/30 text-[#1B2A4E]' : 'bg-white border-stone-200 text-stone-500 hover:bg-stone-50'}`}
                      >
                        <span className="text-sm">{REACTIONS.find(r => r.type === type)?.emoji}</span>
                        <span>{count}</span>
                      </button>
                    );
                  })}

                  <div className="relative">
                    <button
                      onClick={() => setActivePicker(activePicker === comment.id ? null : comment.id)}
                      className="w-6 h-6 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-500 transition-colors"
                      title="Reaccionar"
                    >
                      <SafeIcon name="Smile" className="w-3.5 h-3.5" />
                    </button>

                    {activePicker === comment.id && (
                      <>
                        {/* Invisible backdrop to close the picker when clicking outside */}
                        <div 
                          className="fixed inset-0 z-0" 
                          onClick={() => setActivePicker(null)} 
                        />
                        <div className="absolute top-full mt-1 left-0 z-10 bg-white border border-stone-100 shadow-xl rounded-full px-2 py-1.5 flex gap-1 origin-top-left animate-in fade-in zoom-in duration-200">
                          {REACTIONS.map(r => (
                            <button
                              key={r.type}
                              onClick={async () => {
                                try {
                                  await toggleCommentReaction(comment.id, r.type);
                                  setActivePicker(null);
                                } catch (err) {
                                  console.error(err);
                                }
                              }}
                              className="w-8 h-8 flex items-center justify-center text-xl hover:bg-stone-100 rounded-full transition-transform hover:scale-110"
                              title={r.type}
                            >
                              {r.emoji}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        }) : (
          <div className="text-center py-8 text-stone-400 font-editorial italic bg-stone-50 rounded-2xl border border-stone-100 border-dashed">
            Aún no hay comentarios. Sé el primero en opinar.
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex gap-3">
        <img 
          src={avatarPublicUrl(currentUser.foto_url) || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(currentUser.nombre || '?')}`} 
          alt="" 
          className="w-12 h-12 rounded-full object-cover shrink-0 ring-2 ring-stone-100 hidden sm:block" 
        />
        <div className="flex-1 relative">
          <input
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Escribe un comentario..."
            className="w-full pl-5 pr-14 py-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-sm font-medium"
          />
          <button
            type="submit"
            disabled={!text.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[#1B2A4E] text-white rounded-lg disabled:opacity-50 disabled:bg-stone-300 transition-colors hover:bg-[#253969]"
          >
            <SafeIcon name="Send" className="w-4 h-4 ml-1" />
          </button>
        </div>
      </form>
    </div>
  );
}